import { format } from 'date-fns'
import z from 'zod'

const _isoDateSchema = z.string().date()
export type DateOnly = z.infer<typeof _isoDateSchema>

/**
 * Parses a date string (YYYY-MM-DD format) into a Date object.
 * The date is interpreted as midnight in the user's local timezone so that the date is timezone agnostic.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object at midnight local time
 *
 * @example
 * parseDateOnly('2024-12-12') // Dec 12, 2024 at 00:00:00 local time
 */
export function parseDateOnly(dateString: string): Date {
  return new Date(dateString + 'T00:00:00')
}

/**
 * Formats a Date object into a date string (YYYY-MM-DD format).
 * Uses the local date components instead of UTC to avoids date shifting issues.
 * No matter what timezone the user is in, the formatted date will represent the same calendar date that was picked.
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * const date = new Date('2024-12-12T21:00:00') // Dec 12, 2024 at 9 PM
 * formatDateOnly(date) // '2024-12-12' (not '2024-12-13' like toISOString would give in toronto timezone)
 */
export function formatDateOnly(date: Date): DateOnly {
  return format(date, 'yyyy-MM-dd') as DateOnly
}
